/**
 * Enhanced User Tracking Service
 * Handles comprehensive user interaction tracking and big data storage
 * Integrated with Big Data Cloud Usertracking.js for advanced analytics
 */

import { saveUserTrackingEvent, getUserAnalytics, getContactInteractions, markContactAsProcessed } from '../backend/big-data-cloud/storage/Usertracking.js';

/**
 * Update user connection point status to green (active)
 * @param {string} userId - User ID
 */
async function updateConnectionPointToGreen(userId) {
  try {
    const token = localStorage.getItem('token');
    if (!token || !userId) return;

    const response = await fetch('https://rapid-mind.com/api/users/update-connection-point', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        userId: userId,
        status: 'green'
      })
    });

    if (response.ok) {
      console.log('🔄 [CONNECTION-POINT] Updated user connection point to green:', userId);
    } else {
      console.error('🔄 [CONNECTION-POINT] Failed to update connection point:', response.status);
    }
  } catch (error) {
    console.error('🔄 [CONNECTION-POINT] Error updating connection point:', error);
  }
}

class UserTrackingService {
  constructor() {
    this.trackedEvents = [];
    this.isTracking = false;
    this.trackClicks = true;
    this.trackInputs = true;
    this.trackScrolls = true;
    this.trackPageViews = true;
    this.trackContacts = true;
    this.trackHelp = true;
  }

  /**
   * Initialize tracking for a user
   */
  init(userId = null) {
    this.userId = userId;
    this.sessionId = this.generateSessionId();
    this.startTime = new Date().toISOString();

    if (this.trackClicks) this.initClickTracking();
    if (this.trackInputs) this.initInputTracking();
    if (this.trackScrolls) this.initScrollTracking();
    if (this.trackPageViews) this.initPageViewTracking();
    if (this.trackContacts) this.initContactTracking();
    if (this.trackHelp) this.initHelpTracking();

    this.isTracking = true;
    console.log('🔍 Enhanced user tracking initialized for session:', this.sessionId, 'User:', userId);
  }

  /**
   * Generate unique session ID
   */
  generateSessionId() {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Track click events with enhanced data collection
   */
  initClickTracking() {
    document.addEventListener('click', (event) => {
      const clickData = {
        eventType: 'click',
        timestamp: new Date().toISOString(),
        sessionId: this.sessionId,
        userId: this.userId,
        element: {
          tagName: event.target.tagName,
          id: event.target.id,
          className: String(event.target.className?.baseVal || event.target.className || ''),
          textContent: event.target.textContent?.substring(0, 100),
          href: event.target.href,
          type: event.target.type,
          name: event.target.name,
          value: event.target.value
        },
        position: {
          x: event.clientX,
          y: event.clientY
        },
        page: {
          url: window.location.href,
          title: document.title,
          referrer: document.referrer,
          pathname: window.location.pathname,
          search: window.location.search,
          hash: window.location.hash
        },
        userAgent: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        },
        interaction: {
          button: event.button,
          ctrlKey: event.ctrlKey,
          shiftKey: event.shiftKey,
          altKey: event.altKey,
          metaKey: event.metaKey
        }
      };

      this.trackEvent(clickData);
    });
  }

  /**
   * Track input events with enhanced data collection
   */
  initInputTracking() {
    document.addEventListener('input', (event) => {
      if (event.target.type === 'password') return; // Don't track passwords

      const inputData = {
        eventType: 'input',
        timestamp: new Date().toISOString(),
        sessionId: this.sessionId,
        userId: this.userId,
        element: {
          tagName: event.target.tagName,
          id: event.target.id,
          className: String(event.target.className?.baseVal || event.target.className || ''),
          type: event.target.type,
          name: event.target.name,
          placeholder: event.target.placeholder,
          required: event.target.required
        },
        inputData: {
          value: event.target.value,
          length: event.target.value?.length || 0,
          isEmpty: !event.target.value,
          inputType: event.inputType
        },
        page: {
          url: window.location.href,
          title: document.title,
          formId: event.target.closest('form')?.id,
          formClass: event.target.closest('form')?.className
        }
      };

      this.trackEvent(inputData);
    });
  }

  /**
   * Track scroll events with enhanced data collection
   */
  initScrollTracking() {
    let lastScrollY = 0;
    let scrollTimer = null;

    window.addEventListener('scroll', () => {
      const currentScrollY = window.scrollY;
      const scrollPercentage = Math.round((currentScrollY / (document.body.scrollHeight - window.innerHeight)) * 100);

      // Throttle scroll events
      if (scrollTimer) return;

      scrollTimer = setTimeout(() => {
        const scrollData = {
          eventType: 'scroll',
          timestamp: new Date().toISOString(),
          sessionId: this.sessionId,
          userId: this.userId,
          scrollData: {
            scrollY: currentScrollY,
            scrollPercentage: scrollPercentage,
            direction: currentScrollY > lastScrollY ? 'down' : 'up',
            maxScroll: Math.max(lastScrollY, currentScrollY),
            scrollHeight: document.body.scrollHeight,
            windowHeight: window.innerHeight
          },
          page: {
            url: window.location.href,
            title: document.title,
            scrollHeight: document.body.scrollHeight,
            windowHeight: window.innerHeight
          },
          engagement: {
            timeOnPage: Date.now() - new Date(this.startTime).getTime(),
            scrollDepth: scrollPercentage
          }
        };

        this.trackEvent(scrollData);
        lastScrollY = currentScrollY;
        scrollTimer = null;
      }, 250);
    });
  }

  /**
   * Track page views with enhanced data collection
   */
  initPageViewTracking() {
    const pageViewData = {
      eventType: 'page_view',
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      userId: this.userId,
      page: {
        url: window.location.href,
        title: document.title,
        referrer: document.referrer,
        userAgent: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        },
        screen: {
          width: window.screen.width,
          height: window.screen.height
        },
        performance: {
          loadTime: performance.timing?.loadEventEnd - performance.timing?.navigationStart || 0,
          domContentLoaded: performance.timing?.domContentLoadedEventEnd - performance.timing?.navigationStart || 0
        }
      },
      session: {
        startTime: this.startTime,
        currentTime: new Date().toISOString()
      }
    };

    this.trackEvent(pageViewData);

    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      const visibilityData = {
        eventType: 'visibility_change',
        timestamp: new Date().toISOString(),
        sessionId: this.sessionId,
        userId: this.userId,
        visibility: {
          hidden: document.hidden,
          visibilityState: document.visibilityState,
          timeSpentHidden: document.hidden ? Date.now() : 0
        },
        page: {
          url: window.location.href,
          title: document.title
        }
      };

      this.trackEvent(visibilityData);
    });
  }

  /**
   * Track contact interactions
   */
  initContactTracking() {
    // Track contact form submissions
    document.addEventListener('submit', (event) => {
      const form = event.target;
      const formClassName = String(form?.className?.baseVal || form?.className || '');
      const formId = String(form?.id || '');
      if (form && (formClassName.includes('contact') || formId.includes('contact'))) {
        const contactData = {
          eventType: 'contact',
          timestamp: new Date().toISOString(),
          sessionId: this.sessionId,
          userId: this.userId,
          contactData: {
            formId: form.id,
            formClass: form.className,
            formAction: form.action,
            method: form.method
          },
          page: {
            url: window.location.href,
            title: document.title
          },
          interaction: {
            type: 'form_submission',
            category: 'contact'
          }
        };

        this.trackEvent(contactData);
      }
    });

    // Track contact button clicks
    document.addEventListener('click', (event) => {
      const target = event.target;
      const targetClassName = String(target?.className?.baseVal || target?.className || '');
      const targetId = String(target?.id || '');
      const targetHref = String(target?.href || '');
      if (target && (
        target.textContent?.toLowerCase().includes('contact') ||
        targetClassName.includes('contact') ||
        targetId.includes('contact') ||
        targetHref.includes('contact')
      )) {
        const contactClickData = {
          eventType: 'contact_click',
          timestamp: new Date().toISOString(),
          sessionId: this.sessionId,
          userId: this.userId,
          element: {
            tagName: target.tagName,
            id: target.id,
            className: String(target.className?.baseVal || target.className || ''),
            textContent: target.textContent,
            href: target.href
          },
          page: {
            url: window.location.href,
            title: document.title
          },
          interaction: {
            type: 'contact_initiation',
            category: 'contact'
          }
        };

        this.trackEvent(contactClickData);
      }
    });
  }

  /**
   * Track help interactions
   */
  initHelpTracking() {
    // Track help button clicks
    document.addEventListener('click', (event) => {
      const target = event.target;
      const targetClassName = String(target?.className?.baseVal || target?.className || '');
      const targetId = String(target?.id || '');
      const targetHref = String(target?.href || '');
      if (target && (
        target.textContent?.toLowerCase().includes('help') ||
        targetClassName.includes('help') ||
        targetId.includes('help') ||
        targetHref.includes('help')
      )) {
        const helpClickData = {
          eventType: 'help_click',
          timestamp: new Date().toISOString(),
          sessionId: this.sessionId,
          userId: this.userId,
          element: {
            tagName: target.tagName,
            id: target.id,
            className: String(target.className?.baseVal || target.className || ''),
            textContent: target.textContent,
            href: target.href
          },
          page: {
            url: window.location.href,
            title: document.title
          },
          interaction: {
            type: 'help_request',
            category: 'support'
          }
        };

        this.trackEvent(helpClickData);
      }
    });

    // Track search interactions (as help-seeking behavior)
    document.addEventListener('input', (event) => {
      const target = event.target;
      const targetClassName = String(target?.className?.baseVal || target?.className || '');
      const targetId = String(target?.id || '');
      if (target && (
        targetId.includes('search') ||
        targetClassName.includes('search') ||
        target.placeholder?.toLowerCase().includes('search')
      )) {
        const searchInputData = {
          eventType: 'search_input',
          timestamp: new Date().toISOString(),
          sessionId: this.sessionId,
          userId: this.userId,
          searchData: {
            query: target.value,
            length: target.value?.length || 0,
            isActive: target === document.activeElement
          },
          page: {
            url: window.location.href,
            title: document.title
          },
          interaction: {
            type: 'information_seeking',
            category: 'help'
          }
        };

        this.trackEvent(searchInputData);
      }
    });
  }

  /**
   * Track custom events with enhanced data
   */
  trackEvent(eventData) {
    const enrichedEvent = {
      ...eventData,
      userId: this.userId,
      sessionId: this.sessionId,
      timestamp: new Date().toISOString(),
      appVersion: '1.0.0',
      environment: process.env.NODE_ENV || 'development',
      trackingVersion: '2.0.0'
    };

    this.trackedEvents.push(enrichedEvent);

    // Store in localStorage for immediate access
    this.storeLocally(enrichedEvent);

    // Send to Usertracking.js storage (Big Data Cloud)
    this.saveToBigDataCloud(enrichedEvent);

    // Send to backend if user is authenticated
    this.sendToBackend(enrichedEvent);

    // Update connection point to green when user is active
    if (this.userId) {
      updateConnectionPointToGreen(this.userId);
    }

    console.log('📊 Enhanced event tracked:', enrichedEvent.eventType, 'User:', enrichedEvent.userId);
  }

  /**
   * Save event to Big Data Cloud storage
   */
  async saveToBigDataCloud(eventData) {
    try {
      // Save to Usertracking.js storage
      await saveUserTrackingEvent(eventData);
      console.log('💾 Event saved to Big Data Cloud:', eventData.eventType);
    } catch (error) {
      console.error('❌ Error saving to Big Data Cloud:', error);
      // Store failed events for retry
      this.storeFailedEvent(eventData);
    }
  }

  /**
   * Store event locally
   */
  storeLocally(eventData) {
    try {
      const storedEvents = JSON.parse(localStorage.getItem('userEvents') || '[]');
      storedEvents.push(eventData);

      // Keep only last 1000 events to prevent localStorage bloat
      if (storedEvents.length > 1000) {
        storedEvents.splice(0, storedEvents.length - 1000);
      }

      localStorage.setItem('userEvents', JSON.stringify(storedEvents));
    } catch (error) {
      console.error('Error storing event locally:', error);
    }
  }

  /**
   * Send event to backend
   */
  async sendToBackend(eventData) {
    try {
      const token = localStorage.getItem('token');
      if (!token || !this.userId) return;

      const response = await fetch('https://rapid-mind.com/api/tracking/track-user-interaction', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(eventData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('📡 Event sent to backend:', result);
    } catch (error) {
      console.error('Error sending event to backend:', error);
      // Store failed events for retry
      this.storeFailedEvent(eventData);
    }
  }

  /**
   * Store failed events for retry
   */
  storeFailedEvent(eventData) {
    try {
      const failedEvents = JSON.parse(localStorage.getItem('failedEvents') || '[]');
      failedEvents.push(eventData);
      localStorage.setItem('failedEvents', JSON.stringify(failedEvents));
    } catch (error) {
      console.error('Error storing failed event:', error);
    }
  }

  /**
   * Retry sending failed events
   */
  async retryFailedEvents() {
    try {
      const failedEvents = JSON.parse(localStorage.getItem('failedEvents') || '[]');
      if (failedEvents.length === 0) return;

      const token = localStorage.getItem('token');
      if (!token) return;

      const successfulRetries = [];

      for (const event of failedEvents) {
        try {
          // Retry saving to Big Data Cloud
          await saveUserTrackingEvent(event);

          // Retry sending to backend
          const response = await fetch('https://rapid-mind.com/api/tracking/track-user-interaction', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(event)
          });

          if (response.ok) {
            successfulRetries.push(event);
          }
        } catch (error) {
          console.error('Retry failed for event:', error);
        }
      }

      // Remove successful retries from failed events
      const remainingFailed = failedEvents.filter(event =>
        !successfulRetries.some(retry => retry.timestamp === event.timestamp)
      );

      localStorage.setItem('failedEvents', JSON.stringify(remainingFailed));
      console.log(`🔄 Retried ${successfulRetries.length} failed events`);
    } catch (error) {
      console.error('Error retrying failed events:', error);
    }
  }

  /**
   * Track help provided to user
   */
  trackHelpProvided(helpData) {
    const helpEvent = {
      eventType: 'help_provided',
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      userId: this.userId,
      helpData: {
        type: helpData.type || 'general',
        category: helpData.category || 'support',
        content: helpData.content,
        responseTime: helpData.responseTime,
        helpful: helpData.helpful,
        tags: helpData.tags || []
      },
      page: {
        url: window.location.href,
        title: document.title
      },
      interaction: {
        type: 'help_response',
        category: 'support'
      }
    };

    this.trackEvent(helpEvent);
  }

  /**
   * Track user contact interaction
   */
  trackContactInteraction(contactData) {
    const contactEvent = {
      eventType: 'contact_interaction',
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      userId: this.userId,
      contactData: {
        type: contactData.type || 'general',
        method: contactData.method || 'unknown',
        subject: contactData.subject,
        message: contactData.message,
        responseRequired: contactData.responseRequired !== false,
        priority: contactData.priority || 'normal'
      },
      page: {
        url: window.location.href,
        title: document.title
      },
      interaction: {
        type: 'contact_response',
        category: 'contact'
      }
    };

    this.trackEvent(contactEvent);
  }

  /**
   * Get user behavior analytics with enhanced insights
   */
  getAnalytics() {
    const storedEvents = JSON.parse(localStorage.getItem('userEvents') || '[]');

    const analytics = {
      totalEvents: storedEvents.length,
      sessionId: this.sessionId,
      userId: this.userId,
      startTime: this.startTime,
      endTime: new Date().toISOString(),
      eventTypes: {},
      mostActiveElements: {},
      timeSpent: 0,
      pagesVisited: new Set(),
      clickHeatmap: [],
      interests: {
        searchTerms: [],
        clickedElements: [],
        contactAttempts: 0,
        helpRequests: 0
      },
      behavior: {
        scrollDepth: 0,
        timeOnSite: Date.now() - new Date(this.startTime).getTime(),
        engagementScore: 0
      }
    };

    storedEvents.forEach(event => {
      // Count event types
      analytics.eventTypes[event.eventType] = (analytics.eventTypes[event.eventType] || 0) + 1;

      // Track most active elements
      if (event.element) {
        const elementKey = `${event.element.tagName}.${event.element.id || event.element.className || 'unknown'}`;
        analytics.mostActiveElements[elementKey] = (analytics.mostActiveElements[elementKey] || 0) + 1;
      }

      // Track pages visited
      if (event.page) {
        analytics.pagesVisited.add(event.page.url);
      }

      // Track click positions for heatmap
      if (event.eventType === 'click' && event.position) {
        analytics.clickHeatmap.push({
          x: event.position.x,
          y: event.position.y,
          timestamp: event.timestamp,
          element: event.element?.textContent?.substring(0, 30)
        });
      }

      // Track interests
      if (event.eventType === 'search_input' && event.searchData?.query) {
        analytics.interests.searchTerms.push(event.searchData.query);
      }

      if (event.eventType === 'contact_click' || event.eventType === 'contact') {
        analytics.interests.contactAttempts++;
      }

      if (event.eventType === 'help_click') {
        analytics.interests.helpRequests++;
      }
    });

    analytics.pagesVisited = Array.from(analytics.pagesVisited);

    // Calculate engagement score
    analytics.behavior.engagementScore = this.calculateEngagementScore(analytics);

    return analytics;
  }

  /**
   * Calculate engagement score
   */
  calculateEngagementScore(analytics) {
    let score = 0;

    // Page views (20 points max)
    score += Math.min(analytics.eventTypes.page_view || 0, 20);

    // Interactions (30 points max)
    const interactions = (analytics.eventTypes.click || 0) +
                        (analytics.eventTypes.input || 0) +
                        (analytics.eventTypes.scroll || 0);
    score += Math.min(interactions * 2, 30);

    // Contact/help attempts (25 points max)
    score += Math.min((analytics.interests.contactAttempts + analytics.interests.helpRequests) * 5, 25);

    // Time on site (25 points max)
    const timeInMinutes = analytics.behavior.timeOnSite / (1000 * 60);
    score += Math.min(timeInMinutes * 2, 25);

    return Math.min(score, 100);
  }

  /**
   * Export user data with enhanced analytics
   */
  exportData() {
    const analytics = this.getAnalytics();
    const storedEvents = JSON.parse(localStorage.getItem('userEvents') || '[]');

    return {
      analytics,
      rawEvents: storedEvents,
      exportTimestamp: new Date().toISOString(),
      version: '2.0.0',
      userId: this.userId,
      sessionId: this.sessionId,
      bigDataCloud: {
        integration: 'active',
        storageFiles: [
          'userTracking.json',
          'userInteractions.json',
          'userBehavior.json',
          'userAnalytics.json',
          'userJourney.json',
          'contactInteractions.json'
        ]
      }
    };
  }

  /**
   * Clear tracking data
   */
  clearData() {
    localStorage.removeItem('userEvents');
    localStorage.removeItem('failedEvents');
    this.trackedEvents = [];
    console.log('🗑️ Enhanced user tracking data cleared');
  }

  /**
   * Get user analytics from Big Data Cloud
   */
  async getUserAnalyticsFromCloud(userId) {
    try {
      return await getUserAnalytics(userId);
    } catch (error) {
      console.error('Error getting user analytics from cloud:', error);
      return null;
    }
  }

  /**
   * Get contact interactions from Big Data Cloud
   */
  async getContactInteractionsFromCloud() {
    try {
      return await getContactInteractions();
    } catch (error) {
      console.error('Error getting contact interactions from cloud:', error);
      return [];
    }
  }

  /**
   * Mark contact as processed in Big Data Cloud
   */
  async markContactProcessed(eventId) {
    try {
      return await markContactAsProcessed(eventId);
    } catch (error) {
      console.error('Error marking contact as processed:', error);
      return false;
    }
  }

  /**
   * Get comprehensive user insights
   */
  async getUserInsights(userId) {
    try {
      const [localAnalytics, cloudAnalytics, contactInteractions] = await Promise.all([
        Promise.resolve(this.getAnalytics()),
        this.getUserAnalyticsFromCloud(userId),
        this.getContactInteractionsFromCloud()
      ]);

      return {
        localAnalytics,
        cloudAnalytics,
        contactInteractions: contactInteractions.filter(c => c.userId === userId),
        insights: {
          totalInteractions: localAnalytics.totalEvents,
          engagementScore: localAnalytics.behavior.engagementScore,
          topInterests: [...new Set(localAnalytics.interests.searchTerms)].slice(0, 10),
          contactHistory: contactInteractions.filter(c => c.userId === userId).length,
          helpRequests: localAnalytics.interests.helpRequests,
          mostActivePages: Object.entries(localAnalytics.mostActiveElements)
            .sort(([,a], [,b]) => b - a)
            .slice(0, 5)
            .map(([element, count]) => ({ element, count }))
        },
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error getting user insights:', error);
      return { error: 'Failed to generate insights' };
    }
  }
}

// Create singleton instance
const userTrackingService = new UserTrackingService();

export default userTrackingService;
